import axiosInstanceUser from '@user-plugins/axios/index.js'
import router from '@user-plugins/routes/index.js'
import { defineStore } from 'pinia'
import { toRaw } from 'vue'

import { useAppStore } from '../app'

export const useAuthStore = defineStore('auth', {
    state: () => ({
        authenticated: false,
        access_token: '',
        token_expires_at: null,
        user: {},
        twofactor: true,
        form: {
            username: '',
            password: '',
            remember: false,
        },
        verify: {
            code: '',
        },
        pending_token: '',
        error: {
            status: false,
            message: null,
        },
        resetpassword: {
            email: '',
        },
        changepassword: {
            email: '',
            token: '',
            new_password: '',
            confirm_the_new_password: '',
        },
    }),
    actions: {
        async login() {
            const appStore = useAppStore()
            appStore.setProcessing(true)
            this.error = { status: false, message: null }
            try {
                const { data } = await axiosInstanceUser.post('/login', toRaw(this.form))
                if (data && data.access_token) {
                    this.authenticated = true
                    this.user = data.user
                    this.access_token = data.access_token
                    this.token_expires_at = Math.floor(Date.now() / 1000) + 5 * 3600 // 5 hours in seconds
                    router.push({ name: 'dashboard' })
                } else if (data && data.user) {
                    this.user = data.user
                    this.pending_token = data.pending_token
                    this.twofactor = true
                    router.push({ name: 'verify-2fa' })
                } else {
                    throw new Error('Invalid response from server during login.')
                }
            } catch (error) {
                this.error = {
                    status: true,
                    message: error?.response?.data?.message || 'An unexpected error occurred.',
                }
            } finally {
                appStore.setProcessing(false)
            }
        },

        async verify(code) {
            const appStore = useAppStore()
            appStore.setProcessing(true)
            this.error = { status: false, message: null }
            try {
                const payload = { code: code, pending_token: this.pending_token }
                const { data } = await axiosInstanceUser.post('/verify', payload)
                if (data && data.user && data.access_token) {
                    this.authenticated = true
                    this.user = data.user
                    this.access_token = data.access_token
                    this.token_expires_at = Math.floor(Date.now() / 1000) + 5 * 3600 // 5 hours in seconds
                    router.push({ name: 'dashboard' })
                } else {
                    throw new Error('Invalid response from server during verification.')
                }
            } catch (error) {
                this.error = {
                    status: true,
                    message: error?.response?.data?.message || 'An unexpected error occurred.',
                }
            } finally {
                appStore.setProcessing(false)
            }
        },

        async resend() {
            const appStore = useAppStore()
            appStore.setProcessing(true)
            this.error = { status: false, message: null }
            try {
                const params = { pending_token: this.pending_token }
                await axiosInstanceUser.get('/resend', { params })
            } catch (error) {
                this.error = {
                    status: true,
                    message: error?.response?.data?.message || 'An unexpected error occurred.',
                }
            } finally {
                appStore.setProcessing(false)
            }
        },

        async sendLinkReset() {
            const appStore = useAppStore()
            appStore.setProcessing(true)
            this.error = { status: false, message: null }
            try {
                await axiosInstanceUser.post('/forgot-password', toRaw(this.resetpassword))
            } catch (error) {
                this.error = {
                    status: true,
                    message: error?.response?.data?.message || 'An unexpected error occurred.',
                }
            } finally {
                appStore.setProcessing(false)
            }
        },

        async resolveResetRef(ref) {
            try {
                const { data } = await axiosInstanceUser.post('/resolve-reset-ref', {
                    reference: ref,
                })
                this.changepassword.email = data.email
                this.changepassword.token = data.token
            } catch (error) {
                this.error = {
                    status: true,
                    message:
                        error?.response?.data?.message ||
                        'Le lien de réinitialisation est invalide.',
                }
            }
        },

        async changePassword() {
            const appStore = useAppStore()
            appStore.setProcessing(true)
            this.error = { status: false, message: null }
            try {
                await axiosInstanceUser.post('/change-password', toRaw(this.changepassword))
                router.push({ name: 'login' })
            } catch (error) {
                this.error = {
                    status: true,
                    message: error?.response?.data?.message || 'An unexpected error occurred.',
                }
            } finally {
                appStore.setProcessing(false)
            }
        },

        async logout() {
            try {
                await axiosInstanceUser.post('/logout', toRaw(this.user))
            } catch (error) {
                console.error('Logout failed', error)
            } finally {
                this.forceLogout()
            }
        },

        async fetchUser() {
            try {
                const { data } = await axiosInstanceUser.get('/me')
                if (data && data.user) {
                    this.user = data.user
                }
            } catch (error) {
                if (error?.response?.status === 401) {
                    this.forceLogout()
                }
            }
        },

        forceLogout() {
            this.$reset()
            router.push({ name: 'login' })
        },

        setEmailToken(obj) {
            this.changepassword.email = obj.email
            this.changepassword.token = obj.token
        },
    },
    persist: {
        key: 'payslip-user',
        paths: ['authenticated', 'access_token', 'token_expires_at', 'user', 'verify'],
    },
})
